import {addComparableItem} from 'actionCreators/comparableItems';
import * as listingsActions from 'actionCreators/comparableListings';
import * as rentalsActions from 'actionCreators/comparableRentals';
import * as salesActions from 'actionCreators/comparableSales';
import PlaceholderImage from 'images/no-image@2x.png';
import {arrayOrder} from 'api/util';
import arrayMove from 'array-move';
import * as filterActions from 'ducks/filters';
import ComparablesSorter from 'lib/ComparablesSorter';
import {setIn} from 'lib/immutableObject';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import keys from 'lodash/keys';
import {isEmpty} from 'lodash/lang';
import deepmerge from 'deepmerge';
import { COMPARABLE_TYPE } from '../constants/comparableTypes';

export const initialState = {
  reportType: {value: 'cma'},
  propertyId: null,
  selected: [],
  selectedOrder: [],
  featured: [],
  suggested: [],
  properties: {},
  pagination: {page: 0, size: 0, totalElements: 0, totalPages: 0},
  fetching: false,
  sortBy: 'distance_asc',
  dirty: true,
  selectAll: false,
};

export function getInitialState(comparableType) {
  return {
    ...initialState,
    comparableType,
  };
}

export function comparableActions(type) {
  switch (type) {
    case COMPARABLE_TYPE.SALES:
      return salesActions;
    case COMPARABLE_TYPE.LISTINGS:
      return listingsActions;
    case COMPARABLE_TYPE.RENTALS:
      return rentalsActions;
    default:
      throw new Error(`unexpected type: ${type}`);
  }
}

function updateSelected(state, propertyId, feature, updateWasSelected = true) {
  let selected;
  let selectedOrder;
  if (updateWasSelected) {
    selected = feature ? [propertyId, ...state.selected.filter(p => p !== propertyId)]
      : [...state.selected.filter(p => p !== propertyId), propertyId];
    selectedOrder = state.selectedOrder.length ? [...selected] : [];
  } else {
    selected = [...state.selected];
    selectedOrder = [...state.selectedOrder];
  }
  return {selected, selectedOrder};
}

function getSalePrice(enableUndisclosedPriceInUI, payload, comparableType) {
  if (comparableType === COMPARABLE_TYPE.SALES && payload.isPriceWithheld && !enableUndisclosedPriceInUI) {
    return 0;
  }
  return payload.salePrice || '-';
}
const ComparableItemDuck = (comparableType) => (
      function reducer(state = {
      ...initialState,
      comparableType,
    },action
  ) {
  let actions = comparableActions(state.comparableType);

  switch (action.type) {
    case actions.UPDATE_ATTRIBUTE:
      let newState = state;
      let keyPath = action.payload.keyPath;
      if (typeof keyPath === 'string') {
        keyPath = keyPath.split('.');
      }
      newState = setIn(newState, ['properties', action.payload.propertyId, ...keyPath], action.payload.value);
      let editKeyPath = Array.isArray(action.payload.keyPath) ? `${action.payload.keyPath.join('_')}IsEdited` : `${action.payload.keyPath}IsEdited`;
      newState = setIn(newState, `properties.${action.payload.propertyId}.${editKeyPath}`, true);
      return newState;
    case actions.UPDATE:

      return {
        ...state,
        ...action.payload,
        properties: deepmerge(state.properties, action.payload?.properties || {}),
      };
    case actions.ADD_SELECTED: {
      const newSelectedOrder = state.selectedOrder.length ? addComparableItem(action.payload, state.selectedOrder, state.featured) : [];
      return {
        ...state,
        selectedOrder: newSelectedOrder,
        selected: sortSelectedIfNotMoved(
          addComparableItem(action.payload, state.selected, state.featured),
          state.featured,
          state.properties,
          state.sortBy,
          state.comparableType,
          newSelectedOrder),
      };
    }
    case actions.MOVE_SELECTED:
      const moved = arrayMove([...state.selected], action.payload.oldIndex, action.payload.newIndex);
      return {
        ...state,
        selected: moved,
        selectedOrder: moved,
      };
    case actions.ORDER_SELECTED:
      return {
        ...state,
        selectedOrder: action.payload,
      };
    case actions.REMOVE_SELECTED:
      return {
        ...state,
        selected: state.selected.filter(propertyId => propertyId !== action.payload),
        selectedOrder: state.selectedOrder.filter(propertyId => propertyId !== action.payload),
      };
    case actions.SELECT_ALL:
      const selected = sortSelectedIfNotMoved(
        state.suggested,
        state.suggested.filter(id => state.featured.includes(id)),
        state.properties,
        state.sortBy,
        state.comparableType,
        state.selectedOrder);
      return {
        ...state,
        selected: selected,
        selectAll: true,
      };
    case actions.DESELECT_ALL:
      return {...state, selected: [], selectAll: false, selectedOrder: []};
    case actions.UPDATE_SORT_BY:
      if (action.payload !== state.sortBy) {
        return {
          ...state,
          sortBy: action.payload,
          dirty: true,
          pagination: {...initialState.pagination},
          selected: sortSelectedIfNotMoved(
            state.selected,
            state.featured,
            state.properties,
            action.payload,
            state.comparableType,
            state.selectedOrder),
        };
      } else {
        return state;
      }
    case actions.UPDATE_SUGGESTED:
      return {
        ...state,
        fetching: true,
        suggested: (state.pagination.page === 0 ? [...state.selected] : [...state.suggested]),
      };
    case actions.LOAD_MORE:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          page: state.pagination.page + 1,
        },
        dirty: true,
      };
    case actions.ADD_COMPARABLE: {
      const {enableUndisclosedPriceInUI} = action.flags;

      let preloadedComparableData = state.properties[action.payload.propertyId] || {};
      if (preloadedComparableData.singleLineAddress) {
        preloadedComparableData = {};
      }

      let properties = {
        ...state.properties,
        [action.payload.propertyId]: {
          ...state.properties[action.payload.propertyId],
          ...action.payload,
          salePrice: getSalePrice(enableUndisclosedPriceInUI, action.payload, state.comparableType),
          ...preloadedComparableData,
        },
      };
      if (includes(state.featured, action.payload.propertyId)) {
        properties = updateFeaturedComparable(properties, action.payload.propertyId);
      }

      let newSelectedOrder = [];
      if (state.selectedOrder.length) {
        if (state.selectedOrder.includes(action.payload.propertyId)) {
          newSelectedOrder = [...state.selectedOrder];
        } else {
          newSelectedOrder = [...state.selectedOrder, action.payload.propertyId];
        }
      }
      return {
        ...state,
        properties,
        selectedOrder: newSelectedOrder,
        selected: sortSelectedIfNotMoved(
          [...state.selected, action.payload.propertyId],
          [...state.featured],
          properties,
          state.sortBy,
          state.comparableType,
          newSelectedOrder
        ),
      };
    }
    case filterActions.UPDATE:
      if (isEqual(keys(action.payload), ['errors'])) {
        return state;
      } else {
        return {
          ...state,
          dirty: true,
          pagination: {...initialState.pagination},
        };
      }
    case actions.FEATURE_PROPERTY: {
      const s = updateSelected(state, action.payload.propertyId, true, action.payload.updateSelected);
      return {
        ...state,
        featured: [
          ...state.featured.filter(id => id !== action.payload.propertyId),
          action.payload.propertyId,
        ],
        selected: s.selected,
        selectedOrder: s.selectedOrder,
        properties: updateFeaturedComparable(state.properties, action.payload.propertyId),
      };
    }
    case actions.REMOVE_FEATURE_PROPERTY: {
      const s = updateSelected(state, action.payload, false);
      return {
        ...state,
        featured: [
          ...state.featured.filter(id => id !== action.payload),
        ],
        selected: s.selected,
        selectedOrder: s.selectedOrder,
      };
    }
    case actions.SET_DISPLAY_FIELD: {
      return {
        ...state,
        properties: {
          ...state.properties,
          [action.payload.propertyId]: {
            ...state.properties[action.payload.propertyId],
            ...action.payload,
            displayFields: {
              ...state.properties[action.payload.propertyId].displayFields,
              ...action.payload.displayFields,
            }
          }
        }
      };
    }
    case actions.RESET_COMPARABLE: {
      const {enableUndisclosedPriceInUI} = action.flags ?? {};
      const newProperty = {
        ...state.properties[action.payload.propertyId],
        ...action.payload,
      };
      if (action.payload.salePrice !== undefined) {
        newProperty.salePrice = getSalePrice(enableUndisclosedPriceInUI, action.payload, state.comparableType);
      }
      if (isEmpty(newProperty.propertyPhotoList)) {
        newProperty.propertyPhotoList = [newProperty.propertyPhotoUri || PlaceholderImage];
      }
      Object.keys(action.payload)
        .filter(f => f !== 'propertyId')
        .map(f => `${f}IsEdited`)
        .forEach(f => delete newProperty[f]);
      return {
        ...state,
        properties: {
          ...state.properties,
          [action.payload.propertyId]: newProperty
        },
      };
    }
    case actions.UPDATE_ITEM_FETCHING: {
      const {payload: {fetching: actionFetching, propertyId}} = action;
      const stateFetching = state.properties[propertyId].fetching;
      let newFetching;
      if (isEmpty(actionFetching)) {
        newFetching = {};
      } else {
        newFetching = Object.entries({...stateFetching, ...actionFetching})
          .filter(([_, value]) => value === true)
          .reduce((acc, [key, value]) => ({
            ...acc,
            [key]: value,
          }), {});
      }
      return {
        ...state,
        properties: {
          ...state.properties,
          [action.payload.propertyId]: {
            ...state.properties[action.payload.propertyId],
            fetching: newFetching,
          }
        },
      };
    }
    default:
      return state;
  }
});

export default ComparableItemDuck;

const updateFeaturedComparable = (properties, propertyId) => {
  const property = properties[propertyId];
  if (property && (property.featuredPhotos || []).length === 0 && property.propertyPhotoList) {
    properties = setIn(
      properties,
      [propertyId, 'featuredPhotos'],
      [...property.propertyPhotoList, null, null, null].slice(0, 3)
    );
  }
  return properties;
};

const sortSelectedIfNotMoved = (selected, featured, properties, sortBy, comparableType, selectedOrder) => {
  if (selectedOrder.length) {
    return arrayOrder(selectedOrder, selected);
  }
  return ComparablesSorter.sortedPropertyIds(selected, featured, properties, sortBy, comparableType);
};
